import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, RotateCw } from 'lucide-react';

// In-memory cache for waveform data (persists across component instances)
const waveformCache = new Map();

/**
 * Audio Waveform Player Component
 *
 * Displays an audio waveform visualization with custom playback controls:
 * - Play/Pause button
 * - 10s rewind button
 * - 10s fast-forward button
 * - Clickable waveform for seeking
 * - Current time / duration display
 */
const AudioWaveformPlayer = ({ src, className = '' }) => {
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveformData, setWaveformData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Try to fetch pre-computed waveform, fall back to Web Worker generation
  const generateWaveform = useCallback(async () => {
    if (!src) return;

    // Check cache first (instant)
    if (waveformCache.has(src)) {
      const cached = waveformCache.get(src);
      console.log('[AudioWaveformPlayer] Using cached waveform');
      setWaveformData(cached.peaks);
      if (cached.duration) {
        setDuration(cached.duration);
      }
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Try to fetch pre-computed waveform first (much faster)
      // Waveform JSON is stored alongside audio: final.webm -> final_waveform.json
      const waveformUrl = src.replace(/\.(webm|wav|mp3|m4a)$/i, '_waveform.json');

      try {
        const waveformResponse = await fetch(waveformUrl);
        if (waveformResponse.ok) {
          const waveformJson = await waveformResponse.json();
          if (waveformJson.peaks && waveformJson.peaks.length > 0) {
            console.log('[AudioWaveformPlayer] Using pre-computed waveform');
            // Cache the result
            waveformCache.set(src, { peaks: waveformJson.peaks, duration: waveformJson.duration });
            setWaveformData(waveformJson.peaks);
            if (waveformJson.duration) {
              setDuration(waveformJson.duration);
            }
            setIsLoading(false);
            return;
          }
        }
      } catch (e) {
        // Pre-computed waveform not available, fall back to Web Worker
        console.log('[AudioWaveformPlayer] Pre-computed waveform not found, using Web Worker');
      }

      // Fallback: generate waveform in Web Worker (doesn't block UI)
      const response = await fetch(src);
      const arrayBuffer = await response.arrayBuffer();

      // Check if Web Workers are supported
      if (typeof Worker !== 'undefined') {
        const worker = new Worker('/waveformWorker.js');

        worker.onmessage = (e) => {
          const { success, peaks, duration: workerDuration, error } = e.data;
          if (success && peaks) {
            // Cache the result
            waveformCache.set(src, { peaks, duration: workerDuration });
            setWaveformData(peaks);
            if (workerDuration) {
              setDuration(workerDuration);
            }
          } else {
            console.error('Worker waveform generation failed:', error);
            // Placeholder waveform on failure
            setWaveformData(Array(150).fill(0.3).map(() => 0.2 + Math.random() * 0.6));
          }
          setIsLoading(false);
          worker.terminate();
        };

        worker.onerror = (error) => {
          console.error('Worker error:', error);
          setWaveformData(Array(150).fill(0.3).map(() => 0.2 + Math.random() * 0.6));
          setIsLoading(false);
          worker.terminate();
        };

        // Send audio data to worker
        worker.postMessage({
          audioData: arrayBuffer,
          numPeaks: 150
        }, [arrayBuffer]); // Transfer ownership for performance

      } else {
        // No Web Worker support - fall back to main thread (rare case)
        console.log('[AudioWaveformPlayer] Web Workers not supported, using main thread');
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

        const channelData = audioBuffer.getChannelData(0);
        const samples = 150;
        const blockSize = Math.floor(channelData.length / samples);
        const peaks = [];

        for (let i = 0; i < samples; i++) {
          let sum = 0;
          for (let j = 0; j < blockSize; j++) {
            sum += Math.abs(channelData[i * blockSize + j]);
          }
          peaks.push(sum / blockSize);
        }

        const maxPeak = Math.max(...peaks);
        const normalizedPeaks = peaks.map(p => p / maxPeak);

        // Cache the result
        waveformCache.set(src, { peaks: normalizedPeaks, duration: audioBuffer.duration });
        setWaveformData(normalizedPeaks);
        setDuration(audioBuffer.duration);
        audioContext.close();
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Error generating waveform:', error);
      // Fallback: create a simple placeholder waveform
      setWaveformData(Array(150).fill(0.3).map(() => 0.2 + Math.random() * 0.6));
      setIsLoading(false);
    }
  }, [src]);

  // Draw waveform on canvas
  const drawWaveform = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container || !waveformData) return;

    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = container.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    ctx.scale(dpr, dpr);

    const width = rect.width;
    const height = rect.height;
    const barWidth = 2; // Thin bars
    const barGap = 1; // Small gap between bars
    const totalWaveformWidth = waveformData.length * (barWidth + barGap);
    const startX = (width - totalWaveformWidth) / 2; // Center the waveform
    const progress = duration > 0 ? currentTime / duration : 0;

    ctx.clearRect(0, 0, width, height);

    waveformData.forEach((peak, index) => {
      const x = startX + index * (barWidth + barGap);
      const barHeight = Math.max(2, peak * (height - 8));
      const y = (height - barHeight) / 2;

      // Color based on playback progress
      const isPlayed = index / waveformData.length <= progress;
      ctx.fillStyle = isPlayed ? '#dc2626' : '#1f2937'; // red-600 : gray-800

      ctx.fillRect(x, y, barWidth, barHeight);
    });
  }, [waveformData, currentTime, duration]);

  // Handle play/pause
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
  };

  // Skip forward/backward
  const skip = (seconds) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.currentTime = Math.max(0, Math.min(duration, audio.currentTime + seconds));
  };

  // Handle click on waveform for seeking
  const handleWaveformClick = (e) => {
    const audio = audioRef.current;
    const container = containerRef.current;
    if (!audio || !container || !duration || !waveformData) return;

    const rect = container.getBoundingClientRect();
    const clickX = e.clientX - rect.left;

    // Calculate waveform bounds (same as in drawWaveform)
    const barWidth = 2;
    const barGap = 1;
    const totalWaveformWidth = waveformData.length * (barWidth + barGap);
    const startX = (rect.width - totalWaveformWidth) / 2;
    const endX = startX + totalWaveformWidth;

    // Only seek if click is within waveform bounds
    if (clickX >= startX && clickX <= endX) {
      const progress = (clickX - startX) / totalWaveformWidth;
      audio.currentTime = progress * duration;
    }
  };

  // Initialize waveform
  useEffect(() => {
    generateWaveform();
  }, [generateWaveform]);

  // Draw waveform when data changes
  useEffect(() => {
    drawWaveform();
  }, [drawWaveform]);

  // Redraw on window resize
  useEffect(() => {
    const handleResize = () => drawWaveform();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [drawWaveform]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);
    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, []);

  return (
    <div className={`bg-white rounded-lg p-4 border border-gray-200 ${className}`}>
      {/* Hidden audio element */}
      <audio ref={audioRef} src={src} preload="metadata" />

      {/* Waveform visualization */}
      <div
        ref={containerRef}
        className="relative h-20 mb-4 cursor-pointer"
        onClick={handleWaveformClick}
      >
        {isLoading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-pulse text-text-muted">Loading waveform...</div>
          </div>
        ) : (
          <canvas ref={canvasRef} className="w-full h-full" />
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        {/* Time display */}
        <span className="text-sm font-mono text-text-muted min-w-[90px]">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>

        {/* Playback controls */}
        <div className="flex items-center gap-2">
          {/* Rewind 10s */}
          <button
            onClick={() => skip(-10)}
            className="p-2 rounded-full text-primary hover:bg-primary/10 active:scale-[0.98] transition-all"
            title="Rewind 10 seconds"
          >
            <RotateCcw size={20} />
          </button>

          {/* Play/Pause */}
          <button
            onClick={togglePlay}
            className="p-3 rounded-full text-primary hover:bg-primary/10 active:scale-[0.98] transition-all"
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? <Pause size={28} /> : <Play size={28} className="ml-0.5" />}
          </button>

          {/* Forward 10s */}
          <button
            onClick={() => skip(10)}
            className="p-2 rounded-full text-primary hover:bg-primary/10 active:scale-[0.98] transition-all"
            title="Forward 10 seconds"
          >
            <RotateCw size={20} />
          </button>
        </div>

        {/* Spacer for symmetry */}
        <div className="min-w-[90px]" />
      </div>
    </div>
  );
};

export default AudioWaveformPlayer;
